import { useLayoutEffect, useState } from 'react'

/**
 * Size information on a DOM-Element
 * @typedef {Object} ElementSize
 * @property {number} width - width of the element
 * @property {number} height - height of the element
 * @property {number} inlineWidth - width of the element excluding padding + scrollbar
 * @property {number} inlineHeight - height of the element excluding padding + scrollbar
 * @property {Object} entry - resize-observer entry for the element for advanced usecases
 * - see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry
 */

/** A hook that uses a resize observer to track the size of a certain element.
 * Falls back to bounding client rect. It returns an object containing the outer/inner width and height.
 *
 * @param {Ref} elementRef - ref of the element that should be tracked
 * @returns {ElementSize} Current size of the element
 */
export function useElementSize(elementRef) {
    const [size, setSize] = useState(null)

    const element = elementRef.current

    useLayoutEffect(() => {
        if (typeof window.ResizeObserver !== 'undefined' && elementRef.current) {
            const observer = new ResizeObserver(entries => {
                let width = entries[0]?.borderBoxSize?.[0]?.inlineSize
                let inlineWidth = entries[0]?.contentBoxSize?.[0]?.inlineSize
                let inlineHeight = entries[0]?.contentBoxSize?.[0]?.blockSize
                let height = entries[0]?.borderBoxSize?.[0]?.blockSize

                // polyfill old chrome
                if (entries[0] && !entries[0].borderBoxSize) {
                    const rect = elementRef.current?.getBoundingClientRect() || {}
                    width = rect.width
                    height = rect.height
                }
                if (entries[0] && !entries[0].contentBoxSize) {
                    inlineWidth = width
                    inlineHeight = height
                }

                setSize({ width, height, inlineWidth, inlineHeight, entry: entries[0] })
            })

            observer.observe(elementRef.current)

            // Report width initially. This is needed for children components that render to null
            // and thus will never trigger a resize event.
            const { height, width } = elementRef.current?.getBoundingClientRect() || {}
            if (height && width) {
                setSize({ width, height, inlineHeight: height, inlineWidth: width })
            }

            return () => {
                observer.disconnect()
            }
        }
    }, [element, elementRef])

    return size
}
